const $$splitComponentImporter = () => import('/app/apps/web/src/routes/_app/u/$username.tsx?tsr-split=component');
import { lazyRouteComponent } from '@tanstack/react-router';
const $$splitLoaderImporter = () => import('/app/apps/web/src/routes/_app/u/$username.tsx?tsr-split=loader');
import { lazyFn } from '@tanstack/react-router';
import { createFileRoute } from "@tanstack/react-router";
import { createTranslator } from "use-intl";
import { alternatesToLinks, buildCanonicalAlternates } from "@/i18n/alternates";
import { toAbsoluteUrl } from "@/lib/absolute-url";
import { buildPagePreviewMetaTags } from "@/lib/buildPagePreviewMetaTags";
import { jsonLdScripts } from "@/lib/jsonLdScripts";
import { isIndexablePublicProfile } from "@/lib/non-catalog-seo-policy";
import { withSiteTitle } from "@/lib/page-title";
import { pickEnum, pickString } from "@/lib/search-params";
import { ROLLOUT_NOINDEX_ROBOTS } from "@/lib/seo-meta";
import { getRootLoaderData } from "@/types/intl";
const VALID_TABS = ["overview", "followers", "following", "activity"] as const;
const VALID_STORE_FORMATS = ["all", "vinyl", "cd", "cassette", "video", "other"] as const;
const VALID_STORE_SORTS = ["grade-best", "price-asc", "price-desc", "newest", "artist-az"] as const;
export const Route = createFileRoute("/_app/u/$username")({
  validateSearch: (raw: Record<string, unknown>): ProfileSearch => {
    const pickRaw = (key: string) => raw[key] as string | string[] | undefined;
    return {
      tab: pickEnum(pickRaw("tab"), VALID_TABS),
      search: pickString(pickRaw("search")),
      sort: pickEnum(pickRaw("sort"), VALID_STORE_SORTS),
      format: pickEnum(pickRaw("format"), VALID_STORE_FORMATS),
      recordGrade: pickString(pickRaw("recordGrade")),
      sleeveGrade: pickString(pickRaw("sleeveGrade")),
      genre: pickString(pickRaw("genre")),
      artist: pickString(pickRaw("artist")),
      year: pickString(pickRaw("year")),
      minPrice: pickString(pickRaw("minPrice")),
      maxPrice: pickString(pickRaw("maxPrice"))
    };
  },
  // Keep high-frequency store text search out of loaderDeps so each keystroke
  // does not create a new route match. The loader still reads
  // `location.search.search` on route entry to SSR-prefetch direct filtered
  // profile store loads.
  loaderDeps: ({
    search
  }) => ({
    sort: search.sort,
    format: search.format,
    recordGrade: search.recordGrade,
    sleeveGrade: search.sleeveGrade,
    genre: search.genre,
    artist: search.artist,
    year: search.year,
    minPrice: search.minPrice,
    maxPrice: search.maxPrice
  }),
  loader: lazyFn($$splitLoaderImporter, 'loader'),
  head: async ({
    match,
    loaderData,
    params
  }) => {
    const {
      locale,
      messages
    } = getRootLoaderData(match);
    const tMeta = createTranslator({
      locale,
      messages,
      namespace: "globals.metadata"
    });
    const tProfile = createTranslator({
      locale,
      messages,
      namespace: "globals.profile"
    });
    const path = `/u/${params.username}`;
    const alts = buildCanonicalAlternates(path);
    const profile = loaderData?.profile;
    if (!profile) {
      return {
        meta: [{
          title: withSiteTitle(tMeta("profile"))
        }],
        links: alternatesToLinks(alts)
      };
    }
    const displayName = profile.displayName ?? profile.name ?? params.username;
    const title = tProfile("seoTitle", {
      name: displayName
    });
    const description = profile.bio || tProfile("previewDescription");
    const imageUrl = toAbsoluteUrl(profile.avatarUrl) ?? undefined;
    const url = alts.canonical;
    const indexable = isIndexablePublicProfile(profile);
    return {
      meta: await buildPagePreviewMetaTags({
        title,
        socialTitle: displayName,
        description,
        canonicalUrl: url,
        imageUrl
      }, {
        robots: indexable ? null : ROLLOUT_NOINDEX_ROBOTS,
        openGraph: {
          type: "profile"
        },
        twitter: {
          card: "summary"
        }
      }, {
        locale
      }),
      links: indexable ? alternatesToLinks(alts) : [{
        rel: "canonical",
        href: alts.canonical
      }],
      scripts: jsonLdScripts(indexable ? (await import("@/utils/jsonld")).buildProfileJsonLd(profile) : null)
    };
  },
  component: lazyRouteComponent($$splitComponentImporter, 'component')
});